<template>
  <head>
    <meta charset="utf-8"/>
    <title>Desk Birdy | Liquor License Dashboard</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="County Liquor Management System by Nouveta LTD" name="description"/>
    <meta content="Kelvin Njuguna" name="author"/>
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">
                  <i class="mdi mdi-glass-cocktail text-warning me-2"></i>LIQUOR LICENSE DASHBOARD
                </h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Dashboards</a></li>
                    <li class="breadcrumb-item active">Liquor Licenses</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <ToolBarLiquor/>

          <!-- Main KPI Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Total Businesses</p>
                      <h4 class="mb-0">{{ numberFormat(stats.liquorLicenceCountSummary.potentialBusinesses) }}</h4>
                      <p class="text-muted mb-0 mt-2">
                        <span class="badge badge-soft-info">
                          <i class="mdi mdi-store me-1"></i>Potential Market
                        </span>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                      <span class="avatar-title rounded-circle bg-warning">
                        <i class="bx bx-store font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Active Licenses</p>
                      <h4 class="mb-0">{{ numberFormat(stats.liquorLicenceCountSummary.approvedLicenses) }}</h4>
                      <p class="text-muted mb-0 mt-2">
                        <span class="badge badge-soft-success">
                          <i class="mdi mdi-check-decagram me-1"></i>{{ licensingRate }}% Licensed
                        </span>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                      <span class="avatar-title rounded-circle bg-success">
                        <i class="bx bx-badge-check font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Pending Approval</p>
                      <h4 class="mb-0">{{ numberFormat(stats.liquorLicenceCountSummary.awaitingApproval) }}</h4>
                      <p class="text-muted mb-0 mt-2">
                        <span class="badge badge-soft-warning">
                          <i class="mdi mdi-clock-outline me-1"></i>In Processing
                        </span>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                      <span class="avatar-title rounded-circle bg-warning">
                        <i class="bx bx-time-five font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Expiring Soon (30 Days)</p>
                      <h4 class="mb-0">{{ numberFormat(stats.liquorLicenceCountSummary.expiringIn30Days) }}</h4>
                      <p class="text-muted mb-0 mt-2">
                        <span class="badge badge-soft-danger">
                          <i class="mdi mdi-alert me-1"></i>Needs Renewal
                        </span>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle bg-danger align-self-center mini-stat-icon">
                      <span class="avatar-title rounded-circle bg-danger">
                        <i class="bx bx-error-alt font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Revenue Performance Cards -->
          <div class="row">
            <!-- Today's Collections with Growth -->
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar-xs me-3">
                      <span class="avatar-title rounded-circle bg-primary-subtle text-primary font-size-18">
                        <i class="mdi mdi-calendar-today"></i>
                      </span>
                    </div>
                    <h5 class="font-size-14 mb-0">Today's Collections</h5>
                  </div>
                  <div class="text-muted mt-4">
                    <h4>KES {{ numberFormat(stats.liquorLicenceAmountSummary.todaysCollections) }}</h4>
                    <div class="d-flex">
                      <span :class="growthClass(stats.liquorLicenceAmountSummary.todayGrowth)" class="badge me-2">
                        <i :class="stats.liquorLicenceAmountSummary.todayGrowth >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                        {{ Math.abs(stats.liquorLicenceAmountSummary.todayGrowth) }}%
                      </span>
                      <span class="text-muted small">vs yesterday</span>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">{{ numberFormat(stats.liquorLicenceCountSummary.todaysPayments) }} payments today</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- This Week's Collections -->
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar-xs me-3">
                      <span class="avatar-title rounded-circle bg-success-subtle text-success font-size-18">
                        <i class="mdi mdi-calendar-week"></i>
                      </span>
                    </div>
                    <h5 class="font-size-14 mb-0">This Week</h5>
                  </div>
                  <div class="text-muted mt-4">
                    <h4>KES {{ numberFormat(stats.liquorLicenceAmountSummary.thisWeeksCollections) }}</h4>
                    <div class="d-flex">
                      <span :class="growthClass(stats.liquorLicenceAmountSummary.weekGrowth)" class="badge me-2">
                        <i :class="stats.liquorLicenceAmountSummary.weekGrowth >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                        {{ Math.abs(stats.liquorLicenceAmountSummary.weekGrowth) }}%
                      </span>
                      <span class="text-muted small">vs last week</span>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">Average: KES {{ numberFormat(stats.liquorLicenceAmountSummary.thisWeeksCollections / 7) }}/day</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- This Month's Collections -->
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar-xs me-3">
                      <span class="avatar-title rounded-circle bg-info-subtle text-info font-size-18">
                        <i class="mdi mdi-calendar-month"></i>
                      </span>
                    </div>
                    <h5 class="font-size-14 mb-0">This Month</h5>
                  </div>
                  <div class="text-muted mt-4">
                    <h4>KES {{ numberFormat(stats.liquorLicenceAmountSummary.thisMonthsCollections) }}</h4>
                    <div class="d-flex">
                      <span :class="growthClass(stats.liquorLicenceAmountSummary.monthGrowth)" class="badge me-2">
                        <i :class="stats.liquorLicenceAmountSummary.monthGrowth >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                        {{ Math.abs(stats.liquorLicenceAmountSummary.monthGrowth) }}%
                      </span>
                      <span class="text-muted small">vs last month</span>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted">{{ monthProgress }}% of month elapsed</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Year Collections -->
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center mb-3">
                    <div class="avatar-xs me-3">
                      <span class="avatar-title rounded-circle bg-warning-subtle text-warning font-size-18">
                        <i class="mdi mdi-calendar-check"></i>
                      </span>
                    </div>
                    <h5 class="font-size-14 mb-0">Financial Year</h5>
                  </div>
                  <div class="text-muted mt-4">
                    <h4>KES {{ numberFormat(stats.liquorLicenceAmountSummary.financialYearCollections) }}</h4>
                    <div class="mt-2">
                      <small class="text-muted">Total FY Revenue</small>
                    </div>
                    <div class="mt-2">
                      <div class="progress" style="height: 6px;">
                        <div class="progress-bar bg-warning" role="progressbar"
                             :style="{width: fyProgress + '%'}"
                             :aria-valuenow="fyProgress"
                             aria-valuemin="0"
                             aria-valuemax="100"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Charts Row -->
          <div class="row">
            <!-- Revenue Performance Chart -->
            <div class="col-xl-8">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-chart-line me-2 text-primary"></i>Monthly Revenue Performance
                    </h4>
                    <span class="badge bg-primary-subtle text-primary">Year Comparison</span>
                  </div>
                </div>
                <div class="card-body">
                  <div id="revenuePerformanceChart" style="min-height: 380px;"></div>
                </div>
              </div>
            </div>

            <!-- Payment Modes & Categories -->
            <div class="col-xl-4">
              <div class="card">
                <div class="card-header bg-transparent">
                  <h4 class="card-title mb-0">
                    <i class="mdi mdi-cash-multiple me-2 text-success"></i>Payment Methods
                  </h4>
                </div>
                <div class="card-body">
                  <div id="paymentModesChart" style="min-height: 250px;"></div>

                  <div class="mt-4">
                    <div class="table-responsive">
                      <table class="table table-sm table-nowrap mb-0">
                        <tbody>
                        <tr v-for="(mode, index) in stats.paymentModes" :key="index">
                          <td style="width: 60%">
                            <p class="mb-0">{{ mode.payment_type || 'Other' }}</p>
                          </td>
                          <td style="width: 40%" class="text-end">
                            <h5 class="mb-0">{{ numberFormat(mode.totalAmount) }}</h5>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Performers & Sub-Counties -->
          <div class="row">
            <!-- Top Sub-Counties -->
            <div class="col-xl-6">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-trophy me-2 text-warning"></i>Top Performing Sub-Counties
                    </h4>
                    <span class="badge bg-success">Top 10</span>
                  </div>
                </div>
                <div class="card-body" style="max-height: 500px; overflow-y: auto;">
                  <div v-for="(county, index) in stats.topSubCounties" :key="index" class="mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="d-flex align-items-center">
                        <div class="avatar-xs me-2">
                          <span class="avatar-title rounded-circle" :class="getRankClass(index)">
                            {{ index + 1 }}
                          </span>
                        </div>
                        <div>
                          <h6 class="mb-0">{{ county.subCountyName }}</h6>
                          <small class="text-muted">{{ numberFormat(county.activeBusinesses) }} active businesses</small>
                        </div>
                      </div>
                      <div class="text-end">
                        <h6 class="mb-0">KES {{ numberFormat(county.totalRevenue) }}</h6>
                        <small class="text-muted">{{ numberFormat(county.totalTransactions) }} txns</small>
                      </div>
                    </div>
                    <div class="progress" style="height: 6px;">
                      <div class="progress-bar" :class="getProgressClass(index)"
                           :style="{width: calculatePercentage(county.totalRevenue) + '%'}"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Top Revenue Officers -->
            <div class="col-xl-6">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-account-star me-2 text-info"></i>Top Revenue Officers
                    </h4>
                    <span class="badge bg-info">Top 10</span>
                  </div>
                </div>
                <div class="card-body" style="max-height: 500px; overflow-y: auto;">
                  <div class="table-responsive">
                    <table class="table table-hover table-nowrap align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>#</th>
                        <th>Officer Name</th>
                        <th class="text-center">Transactions</th>
                        <th class="text-end">Total Revenue</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(officer, index) in stats.topOfficers" :key="index">
                        <td>
                          <span class="badge" :class="getRankBadgeClass(index)">{{ index + 1 }}</span>
                        </td>
                        <td>
                          <h6 class="mb-0">{{ officer.createdByName }}</h6>
                          <small class="text-muted">Avg: KES {{ numberFormat(officer.avgTransaction) }}</small>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-primary-subtle text-primary">{{ numberFormat(officer.totalTransactions) }}</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0">{{ numberFormat(officer.totalRevenue) }}</h6>
                          <small class="text-success">Today: {{ numberFormat(officer.todayRevenue) }}</small>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Continued in next file... -->
          <!-- Sub-County Statistics -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-map-marker-multiple me-2 text-primary"></i>Sub-County Performance Analysis
                    </h4>
                    <span class="badge bg-primary">{{ stats.subCountyStatistics.length }} Sub-Counties</span>
                  </div>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover table-striped align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Sub County</th>
                        <th class="text-center">Active Licenses</th>
                        <th class="text-end">This Month</th>
                        <th class="text-end">Last Month</th>
                        <th class="text-center">Growth</th>
                        <th class="text-end">Total Revenue</th>
                        <th class="text-end">Pending</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(item, index) in stats.subCountyStatistics" :key="index">
                        <td>
                          <h6 class="mb-0">{{ item.subCountyName }}</h6>
                          <small class="text-muted">{{ numberFormat(item.totalCount) }} transactions</small>
                        </td>
                        <td class="text-center">
                            <span class="badge bg-success-subtle text-success">
                              {{ numberFormat(item.activeLicenses) }}
                            </span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0">{{ numberFormat(item.currentMonthRevenue) }}</h6>
                          <small class="text-muted">{{ numberFormat(item.currentMonthCount) }} txns</small>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0">{{ numberFormat(item.lastMonthRevenue) }}</h6>
                          <small class="text-muted">{{ numberFormat(item.lastMonthCount) }} txns</small>
                        </td>
                        <td class="text-center">
                            <span v-if="item.lastMonthRevenue > 0"
                                  :class="growthClass(calculateGrowthRate(item.currentMonthRevenue, item.lastMonthRevenue))"
                                  class="badge">
                              <i :class="item.currentMonthRevenue >= item.lastMonthRevenue ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                              {{ Math.abs(calculateGrowthRate(item.currentMonthRevenue, item.lastMonthRevenue)) }}%
                            </span>
                          <span v-else class="badge bg-secondary-subtle text-secondary">N/A</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-primary">{{ numberFormat(item.totalRevenue) }}</h6>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-danger">{{ numberFormat(item.pendingAmount) }}</h6>
                          <small class="text-muted">{{ numberFormat(item.pendingCount) }} pending</small>
                        </td>
                      </tr>
                      </tbody>
                      <tfoot class="table-dark">
                      <tr>
                        <th><strong>TOTAL</strong></th>
                        <th class="text-center"><strong>{{ totalActiveLicenses }}</strong></th>
                        <th class="text-end"><strong>{{ numberFormat(totalCurrentMonth) }}</strong></th>
                        <th class="text-end"><strong>{{ numberFormat(totalLastMonth) }}</strong></th>
                        <th class="text-center">
                            <span :class="growthClass(overallGrowth)" class="badge">
                              {{ overallGrowth >= 0 ? '+' : '' }}{{ overallGrowth }}%
                            </span>
                        </th>
                        <th class="text-end"><strong>{{ numberFormat(totalRevenue) }}</strong></th>
                        <th class="text-end"><strong>{{ numberFormat(totalPending) }}</strong></th>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top License Types -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <h4 class="card-title mb-0">
                    <i class="mdi mdi-star me-2 text-warning"></i>Top Revenue Generating License Types
                  </h4>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>#</th>
                        <th>License Type</th>
                        <th class="text-center">Transactions</th>
                        <th class="text-end">Average Amount</th>
                        <th class="text-end">This Month</th>
                        <th class="text-end">Total Revenue</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(license, index) in stats.topLicenseTypes" :key="index">
                        <td>
                          <span class="badge" :class="getRankBadgeClass(index)">{{ index + 1 }}</span>
                        </td>
                        <td>
                          <h6 class="mb-0">{{ license.incomeTypeDescription }}</h6>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-info-subtle text-info">{{ numberFormat(license.transactionCount) }}</span>
                        </td>
                        <td class="text-end">
                          <span class="text-muted">KES {{ numberFormat(license.avgAmount) }}</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-success">{{ numberFormat(license.currentMonthRevenue) }}</h6>
                        </td>
                        <td class="text-end">
                          <h5 class="mb-0 text-primary">{{ numberFormat(license.totalRevenue) }}</h5>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Daily Trend Chart -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <h4 class="card-title mb-0">
                    <i class="mdi mdi-chart-areaspline me-2 text-success"></i>30-Day Collection Trend
                  </h4>
                </div>
                <div class="card-body">
                  <div id="dailyTrendChart" style="min-height: 320px;"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- License Categories -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-shape me-2 text-info"></i>License Category Breakdown
                    </h4>
                    <span class="badge bg-info">{{ stats.licenseCategories ? stats.licenseCategories.length : 0 }} Categories</span>
                  </div>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Business Category</th>
                        <th class="text-center">Total Businesses</th>
                        <th class="text-center">Approved</th>
                        <th class="text-center">Pending</th>
                        <th class="text-end">Total Revenue</th>
                        <th class="text-center">Approval Rate</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(category, index) in stats.licenseCategories" :key="index">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2">
                                <span class="avatar-title rounded-circle bg-info-subtle text-info">
                                  {{ index + 1 }}
                                </span>
                            </div>
                            <h6 class="mb-0">{{ category.category }}</h6>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-primary-subtle text-primary">{{ numberFormat(category.businessCount) }}</span>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-success-subtle text-success">{{ numberFormat(category.approvedCount) }}</span>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-warning-subtle text-warning">{{ numberFormat(category.pendingCount) }}</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-primary">KES {{ numberFormat(category.totalRevenue) }}</h6>
                        </td>
                        <td class="text-center">
                          <div class="d-flex align-items-center justify-content-center">
                            <span class="me-2">{{ calculateApprovalRate(category.approvedCount, category.businessCount) }}%</span>
                            <div class="progress" style="width: 60px; height: 6px;">
                              <div class="progress-bar" :class="getApprovalRateClass(calculateApprovalRate(category.approvedCount, category.businessCount))"
                                   :style="{width: calculateApprovalRate(category.approvedCount, category.businessCount) + '%'}"></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                      <tfoot class="table-dark">
                      <tr>
                        <th><strong>TOTAL</strong></th>
                        <th class="text-center"><strong>{{ totalCategoryBusinesses }}</strong></th>
                        <th class="text-center"><strong>{{ totalCategoryApproved }}</strong></th>
                        <th class="text-center"><strong>{{ totalCategoryPending }}</strong></th>
                        <th class="text-end"><strong>{{ numberFormat(totalCategoryRevenue) }}</strong></th>
                        <th class="text-center"><strong>{{ overallApprovalRate }}%</strong></th>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6"></div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">Developed by County</div>
            </div>
          </div>
        </div>
      </footer>

    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import Highcharts from 'highcharts';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/accessibility';

Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
    decimalPoint: '.'
  }
});

import NavigationBar from "@/components/Navigation.vue";
import ToolBarLiquor from "@/components/liquor/ToolBarLiquor.vue";
import {execute, liquor, biller} from "@/api";

export default {
  name: "LiquorDashboard",
  components: {ToolBarLiquor, NavigationBar},
  data() {
    return {
      stats: {
        liquorLicenceCountSummary: {
          potentialBusinesses: 0,
          invoicedBusinesses: 0,
          approvedLicenses: 0,
          awaitingApproval: 0,
          expiredLicenses: 0,
          expiringIn30Days: 0,
          todaysApplications: 0,
          todaysPayments: 0
        },
        liquorLicenceAmountSummary: {
          totalRevenue: 0,
          todaysCollections: 0,
          yesterdaysCollections: 0,
          thisWeeksCollections: 0,
          lastWeeksCollections: 0,
          thisMonthsCollections: 0,
          lastMonthsCollections: 0,
          financialYearCollections: 0,
          pendingInvoicesSum: 0,
          avgTransactionAmount: 0,
          todayGrowth: 0,
          weekGrowth: 0,
          monthGrowth: 0
        },
        revenuePerformance: [],
        subCountyStatistics: [],
        topSubCounties: [],
        topOfficers: [],
        paymentModes: [],
        dailyTrend: [],
        topLicenseTypes: [],
        licenseCategories: []
      }
    }
  },
  computed: {
    licensingRate() {
      if (this.stats.liquorLicenceCountSummary.invoicedBusinesses === 0) return 0;
      return ((this.stats.liquorLicenceCountSummary.approvedLicenses /
          this.stats.liquorLicenceCountSummary.invoicedBusinesses) * 100).toFixed(1);
    },
    monthProgress() {
      const now = new Date();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      return ((now.getDate() / daysInMonth) * 100).toFixed(0);
    },
    fyProgress() {
      return Math.min(100, ((this.stats.liquorLicenceAmountSummary.financialYearCollections /
          (this.stats.liquorLicenceAmountSummary.financialYearCollections * 1.2)) * 100).toFixed(0));
    },
    totalActiveLicenses() {
      return this.stats.subCountyStatistics.reduce((sum, item) =>
          sum + parseInt(item.activeLicenses || 0), 0);
    },
    totalCurrentMonth() {
      return this.stats.subCountyStatistics.reduce((sum, item) =>
          sum + parseFloat(item.currentMonthRevenue || 0), 0);
    },
    totalLastMonth() {
      return this.stats.subCountyStatistics.reduce((sum, item) =>
          sum + parseFloat(item.lastMonthRevenue || 0), 0);
    },
    totalRevenue() {
      return this.stats.subCountyStatistics.reduce((sum, item) =>
          sum + parseFloat(item.totalRevenue || 0), 0);
    },
    totalPending() {
      return this.stats.subCountyStatistics.reduce((sum, item) =>
          sum + parseFloat(item.pendingAmount || 0), 0);
    },
    overallGrowth() {
      if (this.totalLastMonth === 0) return 0;
      return ((this.totalCurrentMonth - this.totalLastMonth) / this.totalLastMonth * 100).toFixed(1);
    },
    totalCategoryBusinesses() {
      if (!this.stats.licenseCategories) return 0;
      return this.stats.licenseCategories.reduce((sum, item) =>
          sum + parseInt(item.businessCount || 0), 0);
    },
    totalCategoryApproved() {
      if (!this.stats.licenseCategories) return 0;
      return this.stats.licenseCategories.reduce((sum, item) =>
          sum + parseInt(item.approvedCount || 0), 0);
    },
    totalCategoryPending() {
      if (!this.stats.licenseCategories) return 0;
      return this.stats.licenseCategories.reduce((sum, item) =>
          sum + parseInt(item.pendingCount || 0), 0);
    },
    totalCategoryRevenue() {
      if (!this.stats.licenseCategories) return 0;
      return this.stats.licenseCategories.reduce((sum, item) =>
          sum + parseFloat(item.totalRevenue || 0), 0);
    },
    overallApprovalRate() {
      if (this.totalCategoryBusinesses === 0) return 0;
      return ((this.totalCategoryApproved / this.totalCategoryBusinesses) * 100).toFixed(1);
    }
  },
  mounted() {
    this.getDashboard();
    this.menuAccess();
  },
  methods: {
    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Liquor Dashboard");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller).catch((e) => console.error('Menu access error:', e.message));
    },
    numberFormat(num) {
      if (!num || isNaN(num)) return '0';
      return new Intl.NumberFormat("en-GB").format(num);
    },
    growthClass(growth) {
      if (growth > 0) return 'badge-soft-success';
      if (growth < 0) return 'badge-soft-danger';
      return 'badge-soft-secondary';
    },
    getRankClass(index) {
      if (index === 0) return 'bg-warning text-white';
      if (index === 1) return 'bg-secondary text-white';
      if (index === 2) return 'bg-danger text-white';
      return 'bg-light text-dark';
    },
    getRankBadgeClass(index) {
      if (index < 3) return 'bg-warning';
      if (index < 6) return 'bg-info';
      return 'bg-secondary';
    },
    getProgressClass(index) {
      if (index === 0) return 'bg-warning';
      if (index === 1) return 'bg-secondary';
      if (index === 2) return 'bg-danger';
      return 'bg-primary';
    },
    calculatePercentage(value) {
      const max = Math.max(...this.stats.topSubCounties.map(c => parseFloat(c.totalRevenue || 0)));
      return max > 0 ? (value / max * 100) : 0;
    },
    calculateGrowthRate(current, previous) {
      if (previous === 0) return 0;
      return (((current - previous) / previous) * 100).toFixed(1);
    },
    calculateApprovalRate(approved, total) {
      if (total === 0) return 0;
      return ((approved / total) * 100).toFixed(1);
    },
    getApprovalRateClass(rate) {
      if (rate >= 80) return 'bg-success';
      if (rate >= 60) return 'bg-info';
      if (rate >= 40) return 'bg-warning';
      return 'bg-danger';
    },
    renderRevenuePerformanceChart() {
      const data = this.stats.revenuePerformance;

      Highcharts.chart('revenuePerformanceChart', {
        chart: { type: 'column' },
        title: { text: null },
        xAxis: {
          categories: data.map(item => item[0]),
          crosshair: true
        },
        yAxis: {
          title: { text: 'Revenue (KES)' }
        },
        tooltip: {
          shared: true,
          valuePrefix: 'KES ',
          valueDecimals: 0
        },
        plotOptions: {
          column: {
            pointWidth: 25,
            borderWidth: 0,
            dataLabels: {
              enabled: false
            }
          }
        },
        series: [{
          name: 'Last Year',
          data: data.map(item => parseFloat(item[2])),
          color: '#556ee6'
        }, {
          name: 'This Year',
          data: data.map(item => parseFloat(item[1])),
          color: '#34c38f'
        }]
      });
    },
    renderPaymentModesChart() {
      const data = this.stats.paymentModes.map(mode => ({
        name: mode.payment_type || 'Other',
        y: parseFloat(mode.totalAmount || 0)
      }));

      Highcharts.chart('paymentModesChart', {
        chart: { type: 'pie' },
        title: { text: null },
        tooltip: {
          pointFormat: '<b>KES {point.y:,.0f}</b><br/>{point.percentage:.1f}%'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f}%'
            }
          }
        },
        series: [{
          name: 'Revenue',
          colorByPoint: true,
          data: data
        }]
      });
    },
    renderDailyTrendChart() {
      const dates = this.stats.dailyTrend.map(item => item.collection_date);
      const amounts = this.stats.dailyTrend.map(item => parseFloat(item.daily_amount || 0));

      Highcharts.chart('dailyTrendChart', {
        chart: { type: 'areaspline' },
        title: { text: null },
        xAxis: {
          categories: dates,
          labels: {
            rotation: -45,
            step: 3
          }
        },
        yAxis: {
          title: { text: 'Daily Collections (KES)' }
        },
        tooltip: {
          valuePrefix: 'KES ',
          valueDecimals: 0
        },
        plotOptions: {
          areaspline: {
            fillOpacity: 0.5
          }
        },
        series: [{
          name: 'Daily Collections',
          data: amounts,
          color: '#34c38f',
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(52, 195, 143, 0.3)'],
              [1, 'rgba(52, 195, 143, 0)']
            ]
          }
        }]
      });
    },
    getDashboard() {
      const data = new FormData();
      data.append("function", "getDashboard");
      execute(data, liquor)
          .then((res) => {
            if (res.data.success) {
              this.stats = res.data.data.liquorCertQuickStats;

              // Render charts after data is loaded
              this.$nextTick(() => {
                this.renderRevenuePerformanceChart();
                this.renderPaymentModesChart();
                this.renderDailyTrendChart();
              });
            }
          })
          .catch((e) => console.error('Error:', e.message));
    }
  }
}
</script>

<style scoped>
.mini-stats-wid .card-body {
  padding: 1.5rem;
}

.mini-stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: none;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.table-hover tbody tr:hover {
  background-color: rgba(85, 110, 230, 0.05);
}

.badge-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.18);
}

.badge-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.18);
}

.badge-soft-secondary {
  color: #74788d;
  background-color: rgba(116, 120, 141, 0.18);
}

.badge-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
}

.badge-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
}

.badge-soft-primary {
  color: #556ee6;
  background-color: rgba(85, 110, 230, 0.18);
}

.progress {
  background-color: rgba(85, 110, 230, 0.1);
}

.avatar-xs {
  height: 2rem;
  width: 2rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  font-weight: 600;
}

@media (max-width: 768px) {
  .mini-stat-icon {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }

  .card-body {
    padding: 1rem;
  }
}
</style>